import React from 'react';
import { Mail, MapPin, MessageSquare } from 'lucide-react';
import Section, { SectionTitle, SectionSubtitle } from '../components/ui/Section';
import HeroSection from '../components/ui/HeroSection';
import HeroCard from '../components/ui/HeroCard';
import PipedriveForm from '../components/PipedriveForm';

const ContactPage = () => {
  return (
    <div id="top" className="font-sans text-black">
      {/* Hero Section */}
      <HeroSection
        title="Contact Us"
        description="Interested in learning more about Senti or exploring partnership opportunities? We'd love to hear from you. Our team is ready to provide information about our technology, investment options, or potential collaborations."
        buttonText="Send a Message"
        buttonLink="#contact"
        buttonVariant="primary"
        buttonScrollTo="contact"
        centered={false}
        paddingY="py-32"
      >
        <HeroCard 
          icon={<MessageSquare className="h-20 w-20 text-primary" />}
          title="Let's Connect"
          description="Whether you're interested in partnership, investment, or learning more about our technology, we're here to help."
        />
      </HeroSection>
      
      {/* Contact Form Section */}
      <Section id="contact">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-primary mb-6">Get in Touch</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-secondary mb-1">Email</h3>
                    <p className="text-gray-700">funding@senti-tech.uk</p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-secondary mb-1">Location</h3>
                    <p className="text-gray-700">London, United Kingdom</p>
                  </div>
                  
                  <div className="pt-6">
                    <h3 className="text-lg font-semibold text-secondary mb-3">Connect With Us</h3>
                    <div className="flex space-x-4">
                      {/* Social media icons would go here */}
                      <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-secondary">
                        <span className="sr-only">Twitter</span>
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                        </svg>
                      </a>
                      
                      <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-secondary">
                        <span className="sr-only">LinkedIn</span>
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Contact Form */}
              <div id="contact">
                <h2 className="text-2xl font-bold text-primary mb-6">Send Us a Message</h2>
                <PipedriveForm />
              </div>
            </div>
          </div>
      </Section>
      
      {/* FAQ Section */}
      <Section background="light">
        <div className="max-w-4xl mx-auto">
            <SectionTitle>Frequently Asked Questions</SectionTitle>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-secondary mb-2">What is Senti's Large Biosignal Model?</h3>
                <p className="text-gray-700">Our Large Biosignal Model (LBM) is a pioneering AI technology designed to decode complex signals generated by the human body, enabling early disease detection and personalized treatment approaches.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-secondary mb-2">How can I partner with Senti?</h3>
                <p className="text-gray-700">If you're a medical device manufacturer interested in integrating our LBM into your products, contact us through this form and select "Partnership Opportunities" from the dropdown. Our team will get back to you to discuss how we can work together.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-secondary mb-2">What investment opportunities are available?</h3>
                <p className="text-gray-700">Senti is currently raising a £10 million Series A round. If you have visited our investor section and are appropriately qualified to invest and wish to discus potential opportunities, then please express your interest in the form above and our team will contact you with more information.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-bold text-secondary mb-2">Are there any career opportunities at Senti?</h3>
                <p className="text-gray-700">We're always looking for talented individuals to join our team, especially in AI research, engineering, and healthcare. Please select "Career Opportunities" in the form above and share your background and interests.</p>
              </div>
            </div>
          </div>
      </Section>
    </div>
  );
};

export default ContactPage;
